import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, UserPlus, Store, Users2, KanbanSquare, CheckSquare,
  CalendarDays, Activity, Megaphone, FileText, Repeat, Coins,
  MapPin, BarChart3, UserCog, Plug, Settings, ChevronLeft,
  Inbox, PenSquare, FolderOpen, ShieldCheck, UserRound,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useAttention } from "@/context/AttentionContext";
import api from "@/lib/api";

const COUNT_KEY = { "/leads": "leads", "/tasks": "tasks", "/renewals": "renewals" };

function NavBadge({ count, mini, testid }) {
  if (!count) return null;
  const label = count > 99 ? "99+" : count;
  return (
    <span
      data-testid={`nav-badge-${testid}`}
      className={cn(
        "flex items-center justify-center rounded-full bg-[#FE3967] font-bold text-white tabular-nums leading-none",
        mini
          ? "absolute top-1 right-1 h-[15px] min-w-[15px] px-[3px] text-[9px]"
          : "ml-auto h-[18px] min-w-[18px] px-1.5 text-[10px]"
      )}
    >
      {label}
    </span>
  );
}

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, group: "OVERVIEW" },
  { to: "/leads", label: "Leads", icon: UserPlus, group: "SALES" },
  { to: "/host-businesses", label: "Host Businesses", icon: Store, group: "SALES" },
  { to: "/contacts", label: "Contacts", icon: Users2, group: "SALES" },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, group: "SALES" },
  { to: "/tasks", label: "Tasks & Follow-Ups", icon: CheckSquare, group: "SALES" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, group: "SALES" },
  { to: "/activities", label: "Activities", icon: Activity, group: "SALES" },
  { to: "/composer", label: "Composer", icon: PenSquare, group: "SALES" },
  { to: "/resources", label: "My Resources", icon: Inbox, group: "SALES" },
  { to: "/advertisers", label: "Advertisers", icon: Megaphone, group: "CUSTOMERS" },
  { to: "/contracts", label: "Sales & Contracts", icon: FileText, group: "CUSTOMERS" },
  { to: "/renewals", label: "Renewals", icon: Repeat, group: "CUSTOMERS" },
  { to: "/commissions", label: "Commissions", icon: Coins, group: "MANAGEMENT" },
  { to: "/territories", label: "Markets & Zones", icon: MapPin, group: "MANAGEMENT" },
  { to: "/reports", label: "Reports", icon: BarChart3, group: "MANAGEMENT" },
  { to: "/admin", label: "Admin Control Center", icon: ShieldCheck, admin: true, group: "ADMIN" },
  { to: "/documents", label: "Document Library", icon: FolderOpen, admin: true, group: "ADMIN" },
  { to: "/users", label: "Users", icon: UserCog, admin: true, group: "ADMIN" },
  { to: "/integrations", label: "Integrations", icon: Plug, admin: true, group: "ADMIN" },
  { to: "/settings", label: "Settings", icon: Settings, admin: true, group: "ADMIN" },
];

const GROUP_ORDER = ["OVERVIEW", "SALES", "CUSTOMERS", "MANAGEMENT", "ADMIN"];

export default function Sidebar({ collapsed, onToggle, mobile = false, onNavigate }) {
  const { user } = useAuth();
  const { counts } = useAttention();
  const isAdmin = ["tenant_owner", "platform_owner"].includes(user?.role);
  const canViewTeam = ["tenant_owner", "platform_owner", "sales_manager"].includes(user?.role);
  const isRep = user?.role === "sales_rep";
  const items = NAV.filter((n) => !n.admin || isAdmin);
  const mini = collapsed && !mobile;
  const [team, setTeam] = useState([]);
  const [scopeId, setScopeId] = useState(() => localStorage.getItem("cc_crm_scope") || user?.id || "");

  useEffect(() => {
    if (!user?.id) return;
    if (!localStorage.getItem("cc_crm_scope")) {
      localStorage.setItem("cc_crm_scope", user.id);
      localStorage.setItem("cc_crm_scope_name", user.name || "My CRM");
      setScopeId(user.id);
    }
    if (!canViewTeam) return;
    api.get("/users").then((r) => {
      const reps = (r.data.items || []).filter((u) => u.role === "sales_rep" && u.id !== user.id && u.invite_status !== "Cancelled");
      setTeam(reps);
      const selected = localStorage.getItem("cc_crm_scope");
      const selectedRep = reps.find((u) => u.id === selected);
      if (selected === user.id) localStorage.setItem("cc_crm_scope_name", user.name || "My CRM");
      else if (selectedRep) localStorage.setItem("cc_crm_scope_name", selectedRep.name || "Salesperson");
      else if (selected && selected !== user.id) {
        localStorage.setItem("cc_crm_scope", user.id);
        localStorage.setItem("cc_crm_scope_name", user.name || "My CRM");
        setScopeId(user.id);
      }
    }).catch(() => {});
  }, [user?.id, user?.name, canViewTeam]);

  const switchScope = (id, name) => {
    localStorage.setItem("cc_crm_scope", id);
    localStorage.setItem("cc_crm_scope_name", name || "CRM");
    setScopeId(id);
    if (onNavigate) onNavigate();
    window.location.assign("/");
  };

  const groups = GROUP_ORDER
    .map((g) => ({ title: g, items: items.filter((i) => i.group === g) }))
    .filter((g) => g.items.length);

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "flex h-full flex-col border-r border-slate-200 bg-white transition-[width] duration-200",
        mini ? "w-[76px]" : "w-64"
      )}
    >
      <div className="flex h-16 items-center justify-between px-4 border-b border-slate-200/80 shrink-0">
        {mini ? <Logo compact /> : <Logo />}
        {!mobile && (
          <button
            onClick={onToggle}
            data-testid="sidebar-collapse-btn"
            className="hidden lg:flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2.5 py-4 space-y-5">
        {canViewTeam && (
          <div data-testid="team-crm-switcher">
            {!mini && <p className="px-2.5 mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 select-none">TEAM CRM</p>}
            <div className="space-y-0.5">
              <button
                onClick={() => switchScope(user.id, user.name)}
                title={mini ? "My CRM" : undefined}
                className={cn(
                  "w-full group relative flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-all",
                  mini ? "justify-center px-0" : "px-2.5",
                  scopeId === user.id ? "bg-[#00C9E0]/10 text-[#0094A8] font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )}
                data-testid="team-crm-my"
              >
                <UserRound className="h-[18px] w-[18px] shrink-0" />
                {!mini && <span className="truncate">My CRM</span>}
              </button>
              {team.map((rep) => (
                <button
                  key={rep.id}
                  onClick={() => switchScope(rep.id, rep.name)}
                  title={mini ? rep.name : undefined}
                  className={cn(
                    "w-full group relative flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-all",
                    mini ? "justify-center px-0" : "px-2.5",
                    scopeId === rep.id ? "bg-[#00C9E0]/10 text-[#0094A8] font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  )}
                  data-testid={`team-crm-${rep.id}`}
                >
                  <UserRound className="h-[18px] w-[18px] shrink-0" />
                  {!mini && <span className="truncate">{rep.name}</span>}
                  {!mini && rep.active === false && <span className="ml-auto text-[9px] uppercase tracking-wide text-slate-400">Pending</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {groups.map((group) => (
          <div key={group.title}>
            {!mini && (
              <p className="px-2.5 mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 select-none">
                {group.title}
              </p>
            )}
            {mini && group.title !== "OVERVIEW" && <div className="mx-auto mb-1.5 h-px w-6 bg-slate-100" />}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const badgeKey = COUNT_KEY[item.to];
                const count = badgeKey ? (counts[badgeKey] || 0) : 0;
                const displayLabel = isRep && item.to === "/commissions" ? "My Commissions" : item.label;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    title={mini ? displayLabel : undefined}
                    data-testid={`nav-${displayLabel.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-all duration-150",
                        mini ? "justify-center px-0" : "px-2.5",
                        isActive
                          ? "bg-[#00C9E0]/10 text-[#0094A8] font-semibold"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && !mini && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-[#00C9E0]" />
                        )}
                        <Icon className={cn("h-[18px] w-[18px] shrink-0 transition-colors", isActive && "text-[#00C9E0]")} />
                        {!mini && <span className="truncate">{displayLabel}</span>}
                        <NavBadge count={count} mini={mini} testid={badgeKey} />
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {!mini && (
        <div className="shrink-0 border-t border-slate-200/80 p-3">
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-400">
            ChitChat Sales CRM
          </div>
        </div>
      )}
    </aside>
  );
}
